import React, { useMemo, useState } from 'react';
import type { Member, MemberRole } from '../types';
import {
  Search,
  Mail,
  ArrowLeft,
  Save,
  Trash2,
  Shield,
  User,
  BadgeCheck,
  Download,
  ClipboardList,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../components/AuthProvider';
import { AccessDeniedPage } from '../components/AdminRoute';
import { useApiJson } from '../hooks/useApi';
import { downloadCsv } from '../lib/csv';
import { cn } from '../lib/utils';
import { deleteMember, updateMember } from '../lib/members-api';
import { deletedToast, pushScoringToast } from '../components/common/admin-toasts';
import { usePinDelete } from '../components/common/PinDeleteDialog';

interface MembersDto {
  ok: boolean;
  members: Member[];
}

const ROLES: MemberRole[] = ['user', 'member', 'organizer', 'admin'];

function roleBadgeClass(role: MemberRole): string {
  if (role === 'admin') return 'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]';
  if (role === 'organizer') return 'bg-[var(--color-info-subtle)] text-[var(--color-text-secondary)]';
  if (role === 'member') return 'bg-[var(--color-success-subtle)] text-[var(--color-success-text)]';
  return 'bg-[var(--color-surface-2)] text-[var(--color-text-tertiary)]';
}

function RoleIcon({ role }: { role: MemberRole }) {
  if (role === 'admin') return <Shield className="w-3 h-3" />;
  if (role === 'organizer') return <ClipboardList className="w-3 h-3" />;
  if (role === 'member') return <BadgeCheck className="w-3 h-3" />;
  return <User className="w-3 h-3" />;
}

export function MemberDetailPanel({
  member,
  onBack,
  onSaved,
  onDeleted,
  askDelete,
}: {
  member: Member;
  onBack: () => void;
  onSaved: () => void;
  onDeleted: () => void;
  askDelete: ReturnType<typeof usePinDelete>['ask'];
}) {
  const { member: currentUser } = useAuth();
  const editingSelf = member.uid === currentUser?.uid;
  const [name, setName] = useState(member.name);
  const [phone, setPhone] = useState(member.phone ?? '');
  const [role, setRole] = useState<MemberRole>(member.role);
  const [lmNumber, setLmNumber] = useState(member.lmNumber ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateMember(member.uid, {
        name: name.trim(),
        phone: phone.trim() || null,
        role,
        lmNumber: role === 'member' ? lmNumber.trim() || null : null,
      });
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (member.uid === currentUser?.uid) return;
    askDelete({
      title: `Delete ${member.name}?`,
      description: 'This removes them from the system. This cannot be undone.',
      confirmLabel: 'Delete member',
      run: async (pin) => {
        await deleteMember(member.uid, pin);
        pushScoringToast(deletedToast(member.name));
        onDeleted();
      },
    });
  };

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to list
      </button>

      <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] p-6 space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden shrink-0">
            {member.photoURL ? (
              <img
                src={member.photoURL}
                alt={member.name}
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-2xl font-semibold text-[var(--color-text-secondary)]">
                {member.name[0]}
              </span>
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{member.name}</h2>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">{member.email}</p>
            <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
              Joined {new Date(member.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm outline-none focus:border-[var(--color-accent)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              Phone
            </label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm outline-none focus:border-[var(--color-accent)]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              Role
            </label>
            <select
              value={role}
              disabled={editingSelf}
              onChange={(e) => setRole(e.target.value as MemberRole)}
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm outline-none focus:border-[var(--color-accent)] disabled:opacity-60"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {editingSelf && (
              <p className="text-xs text-[var(--color-text-tertiary)] mt-1.5">You cannot change your own role.</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
              LM Number {role === 'member' && <span className="text-[var(--color-error)]">*</span>}
            </label>
            <input
              type="text"
              value={lmNumber}
              onChange={(e) => setLmNumber(e.target.value)}
              disabled={role === 'user'}
              placeholder={role === 'member' ? 'Required for verified members' : 'Not applicable'}
              className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 px-4 text-sm outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
            />
          </div>
        </div>

        {error && <p className="text-sm text-[var(--color-error)]">{error}</p>}

        <div className="flex flex-wrap gap-3 pt-2">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || (role === 'member' && !lmNumber.trim())}
            className="inline-flex items-center gap-2 bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] text-white font-medium py-2.5 px-5 rounded-[var(--radius-md)] text-sm disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          {member.uid !== currentUser?.uid && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              className="inline-flex items-center gap-2 py-2.5 px-5 rounded-[var(--radius-md)] text-sm font-medium text-[var(--color-error)] hover:bg-[var(--color-error-subtle)] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Remove member
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function MembersPage() {
  const { canAccessAdminRoutes } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const { data, loading, error, mutate } = useApiJson<MembersDto>('/api/members', {
    enabled: canAccessAdminRoutes,
  });

  const [removedUids, setRemovedUids] = useState<Set<string>>(new Set());
  const pinDelete = usePinDelete();
  const members = (data?.members ?? []).filter((member) => !removedUids.has(member.uid));

  const filteredMembers = useMemo(
    () =>
      members.filter(
        (m) =>
          m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (m.lmNumber ?? '').toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [members, searchTerm],
  );

  const selectedMember = selectedUid ? members.find((m) => m.uid === selectedUid) : null;

  const handleExportMembers = () => {
    downloadCsv(
      `members-${new Date().toISOString().slice(0, 10)}.csv`,
      ['UID', 'Name', 'Email', 'Phone', 'Role', 'LM Number', 'Is Admin', 'Joined At'],
      filteredMembers.map((member) => [
        member.uid,
        member.name,
        member.email,
        member.phone ?? '',
        member.role,
        member.lmNumber ?? '',
        member.isAdmin ? 'yes' : 'no',
        member.createdAt,
      ]),
    );
  };

  if (!canAccessAdminRoutes) {
    return (
      <Layout>
        <AccessDeniedPage />
      </Layout>
    );
  }

  if (selectedMember) {
    return (
      <Layout>
        {pinDelete.dialog}
        <MemberDetailPanel
          member={selectedMember}
          onBack={() => setSelectedUid(null)}
          onSaved={async () => {
            await mutate();
            setSelectedUid(null);
          }}
          askDelete={pinDelete.ask}
          onDeleted={() => {
            setRemovedUids((prev) => new Set(prev).add(selectedMember.uid));
            setSelectedUid(null);
            void mutate();
          }}
        />
      </Layout>
    );
  }

    return (
      <Layout>
        {pinDelete.dialog}
        <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            Member Management
          </h1>
          <p className="text-[var(--color-text-secondary)] text-sm mt-1">
            Verify members, assign LM numbers, and manage roles.
          </p>
          {error && (
            <p className="text-[var(--color-error)] text-sm mt-2">Could not refresh member list.</p>
          )}
        </header>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleExportMembers}
            disabled={loading || filteredMembers.length === 0}
            className="inline-flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:bg-[var(--color-surface-2)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--color-text-tertiary)]" />
          <input
            type="text"
            placeholder="Search by name, email, or LM number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-3 pl-10 pr-4 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)]"
          />
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-[var(--color-surface-2)] rounded-[var(--radius-lg)] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-[var(--color-border)] overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-[var(--color-surface-2)] text-xs font-medium text-[var(--color-text-secondary)]">
                <tr>
                  <th className="p-4 font-medium">Member</th>
                  <th className="p-4 font-medium hidden md:table-cell">Email</th>
                  <th className="p-4 font-medium">Role</th>
                  <th className="p-4 font-medium hidden sm:table-cell">LM #</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                <AnimatePresence mode="popLayout">
                  {filteredMembers.map((member) => (
                    <motion.tr
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      key={member.uid}
                      onClick={() => setSelectedUid(member.uid)}
                      className="hover:bg-[var(--color-surface-2)]/50 transition-colors duration-[var(--duration-fast)] cursor-pointer"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-[var(--radius-sm)] bg-[var(--color-surface-2)] flex items-center justify-center overflow-hidden shrink-0">
                            {member.photoURL ? (
                              <img
                                src={member.photoURL}
                                alt={member.name}
                                referrerPolicy="no-referrer"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-sm font-medium">{member.name[0]}</span>
                            )}
                          </div>
                          <span className="font-medium text-sm text-[var(--color-text-primary)]">
                            {member.name}
                          </span>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-[var(--color-text-secondary)] hidden md:table-cell">
                        <span className="inline-flex items-center gap-1.5">
                          <Mail className="w-3.5 h-3.5" />
                          {member.email}
                        </span>
                      </td>
                      <td className="p-4">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[var(--radius-sm)] text-xs font-medium capitalize',
                            roleBadgeClass(member.role),
                          )}
                        >
                          <RoleIcon role={member.role} />
                          {member.role}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-[var(--color-text-secondary)] hidden sm:table-cell">
                        {member.lmNumber ?? '—'}
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredMembers.length === 0 && (
          <div className="text-center py-20 bg-[var(--color-surface-1)] rounded-[var(--radius-lg)] border border-dashed border-[var(--color-border)]">
            <p className="text-[var(--color-text-tertiary)] font-medium">
              No members found matching &ldquo;{searchTerm}&rdquo;
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
}
